import React, { createContext } from "react";
import PropTypes from "prop-types";
import { get } from "utils/api";
import useFetch from "hooks/useFetch";

export const PatientProfileContext = createContext();

const PatientProfileProvider = ({ patientId, children }) => {
  const patientFormAttributesAPI = useFetch({
    apiFn: patientId => get(`patients/${patientId}/form-attributes`),
    defaultValue: {
      company_insurances: [],
      accessible_users: { dmes: [], s3_users: [], compliance_coaches: [], onboarding_coaches: [] },
      company_order_settings: {},
      supply_period_options: []
    }
  });

  const fetchPatientHCPCHistoryAPI = useFetch({
    apiFn: patientId => get(`patients/${patientId}/hcpcs-ordered`),
    defaultValue: { eligibility: [], outreachDate: "" }
  });

  const fetchPatientOrderedItemsAPI = useFetch({
    apiFn: patientId => get(`patients/${patientId}/ordered-items`),
    defaultValue: { currentItems: [], compatibleItems: [] }
  });

  const fetchPatientCompanyInventoryAPI = useFetch({
    apiFn: () => get(`patients/${patientId}/company_inventory`),
    defaultValue: [],
    transformResponse: x => x.inventory ?? []
  });

  const fetchPatientWalkInOrdersAPI = useFetch({
    apiFn: patientId => get(`patients/${patientId}/walk-in-orders`),
    defaultValue: [],
    transformResponse: x => x.walkInOrders ?? []
  });

  React.useEffect(() => {
    patientFormAttributesAPI.fetchData(patientId);
    fetchPatientHCPCHistoryAPI.fetchData(patientId);
    fetchPatientOrderedItemsAPI.fetchData(patientId);
    fetchPatientCompanyInventoryAPI.fetchData(patientId);
    fetchPatientWalkInOrdersAPI.fetchData(patientId);
  }, [patientId]);

  return (
    <PatientProfileContext.Provider
      value={{
        companyInsurances:
          patientFormAttributesAPI.response.company_insurances ?? [],
        accessible_users: patientFormAttributesAPI.response
          .accessible_users ?? {
          dmes: [],
          s3_users: [],
          compliance_coaches: [],
          onboarding_coaches: []
        },
        company_order_settings:
          patientFormAttributesAPI.response.company_order_settings ?? {},
        supply_period_options:
          patientFormAttributesAPI.response.supply_period_options ?? [],
        patientFormAttributesAPI,
        fetchPatientHCPCHistoryAPI,
        currentItems: fetchPatientOrderedItemsAPI.response?.currentItems,
        compatibleItems: fetchPatientOrderedItemsAPI.response?.compatibleItems,
        companyInventory: fetchPatientCompanyInventoryAPI.response,
        fetchPatientWalkInOrdersAPI
      }}
    >
      {children}
    </PatientProfileContext.Provider>
  );
};

PatientProfileProvider.propTypes = {
  patientId: PropTypes.string.isRequired,
  children: PropTypes.node
};

export default PatientProfileProvider;
